import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Form,
  InputGroup,
  Modal,
  Overlay,
  Pagination,
  Table,
} from "react-bootstrap";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faSort,
  faSortDown,
  faSortUp,
} from "@fortawesome/free-solid-svg-icons";
import { Columns, ExternalLink, FileText, Save } from "react-feather";
import { DebounceInput } from "react-debounce-input";
import api from "../../api";
import { useLocation, NavLink } from "react-router-dom";
import * as queryString from "query-string";
import useAuth from "../../hooks/useAuth";

const ColumnList = (props) => {
  const { allColumns, showAllChecked, setShowAllChecked } = props;

  return (
    <React.Fragment>
      <div
        // {...props}
        style={{
          backgroundColor: "var(--bs-body-bg)",
          border: "1px solid black",
          borderRadius: 3,
          maxHeight: "512px",
          ...props.style,
        }}
        className="p-2 overflow-auto"
      >
        <Form.Check
          type={"checkbox"}
          id={"show-all"}
          label={
            <>
              <i>Show all</i>
            </>
          }
          checked={showAllChecked}
          name={"show-all"}
          onChange={(e) => {
            setShowAllChecked(e.target.checked);
            allColumns.forEach((col) => {
              if (!col.isVisibleDisabled) {
                col.toggleHidden(!e.target.checked);
              }
            });
          }}
          style={{ color: "var(--bs-body-color)" }}
        />
        {allColumns.map((col, i) => (
          <div key={`column-${i}`}>
            {!col.isVisibleDisabled && (
              <React.Fragment key={col.id}>
                <Form.Check
                  type={"checkbox"}
                  id={`default2-${col.id}`}
                  label={col.name}
                  checked={col.isVisible}
                  name={col.name}
                  onChange={(e) => {
                    col.toggleHidden(!e.target.checked);
                    if (!e.target.checked) {
                      setShowAllChecked(false);
                    }
                  }}
                  style={{ color: "var(--bs-body-color)" }}
                />
              </React.Fragment>
            )}
          </div>
        ))}
      </div>
    </React.Fragment>
  );
};

const ExportButton = (props) => {
  const { title, onClick } = props;
  const [show, setShow] = useState(false);

  return (
    <>
      <Button
        variant="light"
        onClick={(e) => {
          setShow(true);
        }}
        className="ms-1"
      >
        <div className="d-flex justify-content-between align-items-center">
          <Save />
          <div className="d-none d-xl-inline">{title}</div>
        </div>
      </Button>
      {/* <Button className="me-1 mb-1 mb-sm-0" onClick={() => { setShow(true) }} variant={"primary"} disabled={countNumber <= 0} >{title}</Button> */}
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will create a CSV export file for download. This will be done in
          the background, and may take some time, depending on the size of the
          export. The export will appear in your browser downloads when
          complete.
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={(e) => {
              setShow(false);
              onClick(e);
            }}
            variant={"primary"}
          >
            {title}
          </Button>
          <Button onClick={() => setShow(false)} variant={"secondary"}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ColumnSelector = (props) => {
  const { allColumns } = props;
  const target = useRef(null);
  const [showColumnsPicker, setShowColumnsPicker] = useState(false);
  const [showAllChecked, setShowAllChecked] = useState(true);

  useEffect(() => {
    // Each time we show the columns, workout if we should have the show all checked or not
    // Only work it out if we are showing the columns
    if (!showColumnsPicker) return;

    var newValue = true;

    allColumns.forEach((col) => {
      if (!col.isVisible && !col.isVisibleDisabled) {
        newValue = false;
      }
    });

    setShowAllChecked(newValue);
  }, [showColumnsPicker]);

  return (
    <React.Fragment>
      <Button
        variant="light"
        ref={target}
        onClick={() => setShowColumnsPicker(!showColumnsPicker)}
        className="ms-1"
      >
        <div className="d-flex justify-content-between align-items-center">
          <Columns />
          <div className="d-none d-xl-inline">Columns</div>
        </div>
      </Button>
      <Overlay
        target={target.current}
        show={showColumnsPicker}
        placement="auto"
        rootClose={true}
        rootCloseEvent="click"
        onHide={(e) => {
          setShowColumnsPicker(false);
        }}
      >
        {({
          placement: _placement,
          arrowProps: _arrowProps,
          show: _show,
          popper: _popper,
          hasDoneInitialMeasure: _hasDoneInitialMeasure,
          ...props
        }) => (
          <div
            {...props}
            style={{
              position: "absolute",
              // backgroundColor: 'rgba(255, 100, 100, 0.85)',
              padding: "2px 10px",
              color: "white",
              borderRadius: 3,
              ...props.style,
            }}
          >
            <ColumnList
              allColumns={allColumns}
              showAllChecked={showAllChecked}
              setShowAllChecked={setShowAllChecked}
            />
          </div>
        )}
      </Overlay>
    </React.Fragment>
  );
};

const SaveReport = ({
  title,
  name: initialName,
  allUsers: initialAllUsers,
  onClick,
}) => {
  const [show, setShow] = useState(false);
  const [name, setName] = useState(initialName);
  const [allUsers, setAllUsers] = useState(initialAllUsers);
  const { user } = useAuth();

  useEffect(() => {
    setName(initialName);
    setAllUsers(initialAllUsers);
  }, [initialName, initialAllUsers]);

  return (
    <>
      <Button
        variant="light"
        onClick={(e) => {
          setShow(true);
        }}
        className="ms-1"
      >
        <div className="d-flex justify-content-between align-items-center">
          <FileText />
          <div className="d-none d-xl-inline">{title}</div>
        </div>
      </Button>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="reportDetailsForm.name">
            <Form.Label>Report Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="Enter the name of the report"
            />
          </Form.Group>
          {user?.role !== "User" && (
            <Form.Group className="mb-3" controlId="reportDetailsForm.allUsers">
              <Form.Check.Input
                type={"checkbox"}
                value={allUsers}
                onChange={(e) => {
                  setAllUsers(e.target.checked);
                }}
              />
              <Form.Check.Label className="ms-1">{`All Users`}</Form.Check.Label>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={(e) => {
              setShow(false);
              onClick(e, { reportName: name, allUsers: allUsers });
            }}
            variant={"primary"}
          >
            {title}
          </Button>
          <Button
            onClick={() => {
              setName(initialName);
              setAllUsers(initialAllUsers);
              setShow(false);
            }}
            variant={"secondary"}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const TopBottom = (props) => {
  const {
    tableInstance,
    localPageSize,
    setLocalPageSize,
    controlledCount,
    exportData,
    filter,
    filterResults,
    options,
    reportId,
    reportName,
    reportForAllUsers,
    isReportOwner,
    onReportSave,
  } = props;

  const {
    allColumns,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex, pageSize, sortBy, filters },
  } = tableInstance;

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex justify-content-center">
          <InputGroup size={"sm"}>
            <InputGroup.Text>Show</InputGroup.Text>
            <Form.Select
              size={"sm"}
              value={localPageSize}
              onChange={(e) => {
                setLocalPageSize(e.target.value);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={75}>75</option>
              <option value={100}>100</option>
            </Form.Select>
            <InputGroup.Text>entries per page</InputGroup.Text>
          </InputGroup>
          {options.canPickColumns && (
            <ColumnSelector allColumns={allColumns}></ColumnSelector>
          )}
          {options.canExport && (
            <ExportButton
              title={"Export"}
              onClick={(e) => {
                exportData(pageIndex, pageSize, sortBy, filters);
              }}
            ></ExportButton>
          )}
          {isReportOwner && options.reportType && (
            <SaveReport
              title={<span>{reportId ? "Update" : "Save"}&nbsp;Report</span>}
              name={reportName}
              allUsers={reportForAllUsers}
              onClick={onReportSave}
            />
          )}
        </div>

        {controlledCount > 0 && (
          <div>{`Showing entries ${pageIndex * pageSize + 1} to ${
            (pageIndex + 1) * pageSize
          } of ${controlledCount}`}</div>
        )}
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <NavLink to={"/reports"}>
              <Button variant="light" className="me-1">
                <div className="d-flex justify-content-between align-items-center">
                  <FileText />
                  <div className="d-none d-xl-inline">
                    Saved&nbsp;Reports&nbsp;
                    <ExternalLink size={"1.1em"} />
                  </div>
                </div>
              </Button>
            </NavLink>
          </div>
          {options.canFilter && (
            <div>
              {" "}
              <DebounceInput
                style={props.type === "dashboard" ? { width: "70%" } : {}}
                className="table-search debounceInput-search text-gray"
                minLength={0}
                value={filter}
                debounceTimeout={500}
                onChange={(event) =>
                  filterResults && filterResults(event.target.value)
                }
              />
            </div>
          )}
          <Pagination className="m-0 p-0">
            <Pagination.Item
              onClick={(e) => {
                gotoPage(0);
              }}
              disabled={!canPreviousPage}
            >
              <FontAwesomeIcon fixedWidth icon={faAnglesLeft} />
            </Pagination.Item>
            <Pagination.Item
              onClick={(e) => {
                previousPage();
              }}
              disabled={!canPreviousPage}
            >
              <FontAwesomeIcon fixedWidth icon={faAngleLeft} />
            </Pagination.Item>
            <Pagination.Item>
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ border: 0, width: "60px" }}
              >
                <div>{pageIndex + 1}</div>
              </div>
            </Pagination.Item>
            <Pagination.Item
              onClick={(e) => {
                nextPage();
              }}
              disabled={!canNextPage}
            >
              <FontAwesomeIcon fixedWidth icon={faAngleRight} />
            </Pagination.Item>
            <Pagination.Item
              onClick={(e) => {
                gotoPage(pageCount - 1);
              }}
              disabled={!canNextPage}
            >
              <FontAwesomeIcon fixedWidth icon={faAnglesRight} />
            </Pagination.Item>
          </Pagination>
        </div>
      </div>
    </React.Fragment>
  );
};

const SearchTable = ({
  columns,
  data,
  fetchData,
  exportData,
  count: controlledCount,
  options: { canPickColumns = true, canExport = true, ...restOptions } = {
    canPickColumns: true,
    canExport: true,
  },
  reportType = undefined,
}) => {
  var searchTableOptions = {
    ...{ canPickColumns: canPickColumns ?? "", canExport },
    ...{ reportType },
    ...restOptions,
  };

  // var searchTableOptions = {};
  const location = useLocation();
  var { reportid: reportId } = useMemo(() => {
    return queryString.parse(location.search);
  }, [location]);

  const { user } = useAuth();

  const [localPageSize, setLocalPageSize] = useState(100);
  // const [haveData, setHaveData] = useState();
  const [mounted, setMounted] = useState(false);
  const [filter, setFilter] = useState("");

  const [reportName, setReportName] = useState(null);
  const [reportForAllUsers, setReportForAllUsers] = useState(false);
  const [isReportOwner, setIsReportOwner] = useState(true);

  const filterResults = (filterValue) => {
    setFilter(filterValue);
  };

  // const columns2 = columns.map((value) => {
  //   return { ...value, filterValue: "$TEXT=hello" };
  // });

  const initialStateRef = useRef({
    hiddenColumns: columns
      .filter((_) => {
        return _.isVisibleDisabled;
      })
      .map((_) => {
        return _.accessor;
      }),
  });

  var tableInstance = useTable(
    {
      defaultCanSort: false,
      defaultCanFilter: false,
      manualPagination: true,
      manualFilters: true,
      manualSortBy: true,
      columns: columns,
      data: data,
      autoResetHiddenColumns: false,
      autoResetFilters: false,
      pageCount: Math.ceil(controlledCount / localPageSize),
      // initialState: initialState,
      // initialState: {
      //   filters: [
      //     {
      //       id: "category",
      //       value: "°°>SRL Eurolight Signal"
      //     },
      //     {
      //       id: "legacy_id",
      //       value: "SRL"
      //     }
      //   ]
      // },
      // stateReducer: (newState, action, prevState) => {
      //   console.log("Table update", { newState, action, prevState });
      // },
      initialState: initialStateRef.current,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setPageSize,
    state: { pageIndex, pageSize, sortBy, filters },
  } = tableInstance;

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(async () => {
    if (!tableInstance || !reportId) return;

    try {
      // Load & set properties from saved report
      var result = await api.get(`/reports/${reportId}`);

      setReportName(result.data.name);
      setReportForAllUsers(result.data.allUsers);
      setIsReportOwner(result.data.ownerId === user.id);

      setLocalPageSize(result.data.pageSize);

      var reportFilters = result.data.columns
        .filter((v) => {
          return v.filterValue !== null && typeof v.filterValue !== "undefined";
        })
        .map((v) => {
          return { id: v.columnId, value: v.filterValue };
        });

      tableInstance.setAllFilters(reportFilters);

      var sortBy = result.data.columns
        .filter((v) => {
          return v.isSorted;
        })
        .map((v) => {
          return { id: v.columnId, desc: v.isSortedDesc };
        });
      tableInstance.setSortBy(sortBy);

      var hidden = result.data.columns
        .filter((v) => {
          var foundColumn = columns.find((_) => {
            return _.accessor === v.columnId;
          });
          return !v.isVisible || (foundColumn && foundColumn.isVisibleDisabled);
        })
        .map((v) => {
          return v.columnId;
        });
      tableInstance.setHiddenColumns([
        ...hidden,
        ...initialStateRef.current.hiddenColumns,
      ]);

      return () => {
        tableInstance.setHiddenColumns([]);
        tableInstance.setSortBy([]);
        tableInstance.setAllFilters([]);
      };
    } catch (error) {
      return;
    }
  }, [tableInstance, reportId]);

  useEffect(() => {
    if (mounted) {
      fetchData(pageIndex, pageSize, sortBy, filters, filter);
    }
  }, [fetchData, pageIndex, pageSize, sortBy, filters, filter]);

  useEffect(() => {
    setPageSize(localPageSize);
  }, [localPageSize]);

  const handleReportSave = useCallback(async (e, { reportName, allUsers }) => {
    var reportToSave = {
      type: reportType,
      name: reportName ?? "?",
      allUsers: allUsers ?? false,
      pageSize: pageSize,
      columns: tableInstance.allColumns.map((current) => {
        let n = {};
        n.columnId = current.id ?? "?";
        n.isSorted = current.isSorted;
        n.isSortedDesc = current.isSortedDesc ?? false;
        n.isVisible = current.isVisible;
        n.filterValue = current.filterValue ?? null;
        return n;
      }),
    };
    if (reportId) {
      api.put("reports", { ...reportToSave, id: reportId });
    } else {
      api.post("reports", reportToSave);
    }
  }, []);

  const topBottom = (
    <TopBottom
      tableInstance={tableInstance}
      localPageSize={localPageSize}
      setLocalPageSize={setLocalPageSize}
      controlledCount={controlledCount}
      exportData={exportData}
      options={searchTableOptions}
      filter={filter}
      filterResults={filterResults}
      reportId={reportId}
      reportName={reportName}
      reportForAllUsers={reportForAllUsers}
      isReportOwner={isReportOwner}
      onReportSave={handleReportSave}
    />
  );

  return (
    <>
      {topBottom}
      <div style={{ maxHeight: "60vh", overflow: "auto" }}>
        <Table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, headerGroupIndex) => (
              <React.Fragment
                key={"headerGroupFragment" + headerGroupIndex.toString()}
              >
                <tr
                  {...headerGroup.getHeaderGroupProps(() => {
                    return { key: { i: headerGroupIndex } };
                  })}
                  className="badge-soft-secondary"
                >
                  {headerGroup.headers.map((column, columnIndex) => (
                    // Add the sorting props to control sorting. For this example
                    // we can add them into the header props
                    <th
                      {...column.getHeaderProps()}
                      style={{
                        width: column.totalWidth,
                        minWidth: column.totalWidth,
                      }}
                    >
                      {/* <div className="w-100">{column.canFilter ? column.render('Filter', { "width": "100%" }) : <></>}</div> */}
                      <div className="">
                        {column.canFilter ? column.render("Filter") : <></>}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column, columnIndex) => (
                    // Add the sorting props to control sorting. For this example
                    // we can add them into the header props
                    <th {...column.getHeaderProps()}>
                      {/* <div className="w-100">{column.canFilter ? column.render('Filter', { "width": "100%" }) : <></>}</div> */}
                      {/* <div className="">{column.canFilter ? column.render('Filter') : <></>}</div> */}
                      <div className="pt-2">
                        <div
                          {...column.getSortByToggleProps()}
                          className="d-flex justify-content-between align-items-center"
                        >
                          {column.render("Header")}
                          {/* Add a sort direction indicator */}
                          {!column.disableSortBy && (
                            <div>
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <FontAwesomeIcon
                                    fixedWidth
                                    icon={faSortDown}
                                  />
                                ) : (
                                  <FontAwesomeIcon fixedWidth icon={faSortUp} />
                                )
                              ) : (
                                <FontAwesomeIcon fixedWidth icon={faSort} />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </React.Fragment>
            ))}
          </thead>
          {controlledCount > 0 && (
            <tbody {...getTableBodyProps()} style={{ minHeight: "400px" }}>
              {rows.map((row, i) => {
                prepareRow(row);
                return (
                  <tr key={i} {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          )}
        </Table>
      </div>
      {controlledCount === 0 && (
        <div className="w-100 p-3 text-center mt-3 mb-3 bg-light">
          {"No data available in table"}
        </div>
      )}
      {topBottom}
    </>
  );
};

export default SearchTable;
